/**
 * FORM CHECKBOX COMPONENT
 *
 * Reusable checkbox with consistent styling.
 * Supports labels and help text.
 *
 * Features:
 * - Dark/light variants
 * - Label on right side
 * - Help text/description
 * - Toggle switch variant
 *
 * @example
 * <FormCheckbox
 *   label="Mark as favorite"
 *   checked={isFavorite}
 *   onChange={(e) => setIsFavorite(e.target.checked)}
 * />
 *
 * <FormCheckbox
 *   label="Enable notifications"
 *   description="Receive updates about your wishlist items"
 *   variant="dark"
 * />
 */

import { type InputHTMLAttributes, forwardRef } from 'react';

export type CheckboxVariant = 'light' | 'dark';

export interface FormCheckboxProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> {
  /** Label text next to checkbox */
  label: string;
  /** Description text below label */
  description?: string;
  /** Color variant */
  variant?: CheckboxVariant;
  /** Additional class for wrapper */
  wrapperClassName?: string;
  /** Additional class for checkbox */
  className?: string;
}

const variantClasses: Record<CheckboxVariant, { checkbox: string; label: string; description: string }> = {
  light: {
    checkbox: 'rounded border-gray-300 text-purple-600 focus:ring-purple-500 bg-white',
    label: 'text-gray-700',
    description: 'text-gray-500',
  },
  dark: {
    checkbox: 'rounded border-slate-600 text-purple-600 focus:ring-purple-500 bg-slate-700/50',
    label: 'text-glass-secondary',
    description: 'text-glass-muted',
  },
};

export const FormCheckbox = forwardRef<HTMLInputElement, FormCheckboxProps>(
  (
    {
      label,
      description,
      variant = 'light',
      wrapperClassName = '',
      className = '',
      id,
      ...props
    },
    ref
  ) => {
    const checkboxId = id || label.toLowerCase().replace(/\s+/g, '-');
    const styles = variantClasses[variant];

    const checkboxClasses = [
      'h-4 w-4 transition-colors',
      styles.checkbox,
      className,
    ]
      .filter(Boolean)
      .join(' ');

    return (
      <div className={`flex items-start ${wrapperClassName}`}>
        <div className="flex items-center h-5">
          <input
            ref={ref}
            type="checkbox"
            id={checkboxId}
            className={checkboxClasses}
            {...props}
          />
        </div>
        <div className="ml-3">
          <label
            htmlFor={checkboxId}
            className={`text-sm font-medium cursor-pointer ${styles.label}`}
          >
            {label}
          </label>
          {description && (
            <p className={`text-sm ${styles.description}`}>{description}</p>
          )}
        </div>
      </div>
    );
  }
);

FormCheckbox.displayName = 'FormCheckbox';

export default FormCheckbox;
